'use client';

// The shared "game look" (2026-09-29): the chunky, colourful style of the
// Reddit image ads brought into the real games, first in Quick Minds and then
// game by game. Presentation only; each game keeps its own rules. The motion
// classes live in globals.css ("GAME LOOK") and stand still under reduced
// motion and the Reduce Effects switch.
//
// Light mode: colours come from the neutral Tailwind tokens (white, slate),
// which the light theme inverts, or from solid hues, which it keeps; a banner
// row is a dark surface in both themes.
import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useReducedEffects } from '@/firebase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SHOP_ITEMS_DATA } from '@/lib/shop-data';
import { cn, resolveAvatarUrl, getBannerStyles, getNameEffectClasses } from '@/lib/utils';
import { PlayerAvatar } from './player-avatar';

/** A game's name in its header: the display face, big, in the accent colour. */
export const GAME_TITLE_CLASS = 'gnc-game-display text-3xl sm:text-4xl leading-none text-primary drop-shadow-[0_3px_0_rgba(0,0,0,0.45)]';

/** The chunky card a question, a word or a statement sits on. Dark: the owner found white too bright. */
export const GAME_CARD_CLASS = 'rounded-3xl border-[3px] border-slate-950 bg-slate-900 text-white shadow-[0_6px_0_rgba(0,0,0,0.55)]';

/** The letter on each answer card, in the order the choices come. */
export const GAME_CHOICE_KEYS = ['A', 'B', 'C', 'D', 'E', 'F'];

// Solid hues are "true surfaces" in light mode (tailwind.config.ts), so the
// dark letters on them read the same in both modes.
const SWATCHES = ['bg-cyan-300', 'bg-pink-300', 'bg-amber-300', 'bg-lime-300', 'bg-violet-300', 'bg-orange-300'];

export const gameChoiceSwatch = (index: number) => SWATCHES[index % SWATCHES.length];

/**
 * A steady colour per player, so a friend's dot is the same in every round and every game.
 * Takes anything: a roster entry rebuilt by a late write has no uid, and a throw here
 * takes the whole game down for every viewer (2026-10-02, Pictionary in room WLUS).
 */
export function gamePlayerSwatch(uid: unknown): string {
  const key = typeof uid === 'string' ? uid : '';
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return SWATCHES[hash % SWATCHES.length];
}

/** The first character of a name (a whole emoji or accented letter), or "?" for a roster entry without one. */
export function gameInitial(name: unknown): string {
  const first = Array.from(typeof name === 'string' ? name.trim() : '')[0];
  return first ? first.toUpperCase() : '?';
}

/**
 * A player's small round face: their equipped avatar picture when they own
 * one, else their initial on their colour (also while the picture loads, or
 * if it fails).
 */
export function GamePlayerDot({ uid, player, className, fallbackClassName }: {
  uid: string;
  player: any;
  className?: string;
  /** Classes for the initial (its size, for a big face). */
  fallbackClassName?: string;
}) {
  const url = resolveAvatarUrl(player, SHOP_ITEMS_DATA) || undefined;
  return (
    <Avatar className={cn('h-7 w-7 border-2 border-slate-950', className)}>
      {url && <AvatarImage src={url} alt="" className="object-cover" />}
      <AvatarFallback className={cn('text-xs font-black text-slate-950', gamePlayerSwatch(uid || player?.uid), fallbackClassName)}>
        {gameInitial(player?.displayName)}
      </AvatarFallback>
    </Avatar>
  );
}

/** A number that counts up to its new value (600 ms), or jumps there when effects are reduced. */
export function GameCountUp({ value }: { value: number }) {
  const { reduceEffects } = useReducedEffects();
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const still = reduceEffects || (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    if (still || from.current === value) {
      from.current = value;
      setShown(value);
      return;
    }
    const start = performance.now();
    const a = from.current;
    let frame = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / 600);
      setShown(Math.round(a + (value - a) * (1 - Math.pow(1 - k, 3))));
      if (k < 1) frame = requestAnimationFrame(step);
      else from.current = value;
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      from.current = value;
    };
  }, [value, reduceEffects]);
  return <>{shown}</>;
}

/**
 * The round clock: a thick bar and a seconds chip that turns red and ticks
 * once `urgentAt` seconds are left.
 */
export function GameTimerBar({ secondsLeft, fraction, label, urgentAt = 5, className }: {
  secondsLeft: number;
  /** How much of the round is left, 0 to 1. */
  fraction: number;
  label?: ReactNode;
  urgentAt?: number;
  className?: string;
}) {
  const isUrgent = secondsLeft > 0 && secondsLeft <= urgentAt;
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  return (
    <div className={cn('w-full space-y-1.5', className)}>
      {label && (
        <p className={cn('text-left text-[11px] font-bold', isUrgent ? 'text-rose-400' : 'text-white/60')}>{label}</p>
      )}
      <div className="flex items-center gap-3">
        <div className="h-4 flex-1 overflow-hidden rounded-full border-[3px] border-slate-950 bg-slate-800">
          <div
            className={cn('h-full rounded-full transition-all duration-200 ease-linear', isUrgent ? 'bg-rose-400' : 'bg-gradient-to-r from-cyan-300 to-lime-300')}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className={cn(
          'gnc-game-display min-w-[3.25rem] rounded-xl border-[3px] border-slate-950 px-2 py-0.5 text-center text-lg tabular-nums text-slate-950',
          isUrgent ? 'bg-rose-400 gnc-game-tick' : 'bg-slate-900 text-white',
        )}>
          {Math.max(0, secondsLeft)}s
        </span>
      </div>
    </div>
  );
}

/** Who has locked in: a face per friend as their answer lands, and the count line beside them. */
export function GameLockedIn({ ids, players, label, className }: {
  ids: string[];
  players: Record<string, any> | null | undefined;
  label: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-center gap-3', className)}>
      {ids.length > 0 && (
        <span className="flex pl-1.5">
          {ids.slice(0, 8).map((id) => (
            <GamePlayerDot key={id} uid={id} player={players?.[id]} className="gnc-game-in -ml-1.5" />
          ))}
        </span>
      )}
      <span className="text-xs font-black uppercase tracking-widest text-white/60">{label}</span>
    </div>
  );
}

/** Points in a chunky chip: green when earned, plain otherwise. */
export function GamePointsChip({ children, good, className }: { children: ReactNode; good?: boolean; className?: string }) {
  return (
    <span className={cn(
      'gnc-game-display relative shrink-0 rounded-lg border-2 border-slate-950 px-2 py-0.5 text-sm text-slate-950',
      good ? 'bg-emerald-300' : 'bg-slate-800 text-white',
      className,
    )}>
      {children}
    </span>
  );
}

/**
 * One player's row in a round's results or the standings: the avatar, frame,
 * banner and name effect they own, as in the lobby's player list. The banner
 * is the row's background, with a dark shade behind the text so every banner
 * reads.
 */
export function GameResultRow({ uid, player, detail, right, className }: {
  uid: string;
  player: any;
  /** Lines under the name: their answer, a breakdown. Use text-white + opacity for faint text (see below). */
  detail?: ReactNode;
  /** The right-hand side, usually a GamePointsChip. */
  right?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative flex items-center justify-between gap-3 overflow-hidden rounded-2xl border-2 border-white/10 p-3',
        player?.activeBanner ? getBannerStyles(player.activeBanner) : 'bg-white/5',
        className,
      )}
    >
      {player?.activeBanner && (
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/65 to-slate-950/10" />
      )}
      <div className="relative flex min-w-0 flex-1 items-center gap-3">
        <PlayerAvatar player={{ ...player, uid }} size="md" className="shrink-0" />
        <div className="min-w-0 flex-1 text-left">
          {/* The chip sits beside the name, so the lines under it get the row's full
              width: beside everything it squeezed a breakdown into one word per line
              on a small phone. */}
          <div className="flex items-start justify-between gap-2">
            <span className={cn('block min-w-0 truncate text-sm font-bold text-white', player?.activeNameEffect && getNameEffectClasses(player.activeNameEffect))}>
              {player?.displayName}
            </span>
            {right && <div className="flex shrink-0">{right}</div>}
          </div>
          {detail}
        </div>
      </div>
    </div>
  );
}

/**
 * A player in a list (friends, a picker, a streak): the banner they own behind
 * the row, their avatar and frame, their name effect, a line under the name and
 * controls on the right. Without a banner the row is a plain dark tile.
 */
export function GamePlayerRow({ uid, player, detail, badge, actions, dimmed, compact, className, ...rest }: {
  uid: string;
  player: any;
  /** The line under the name. Use GAME_FAINT_ON_ROW for faint text. */
  detail?: ReactNode;
  /** Pinned to the avatar's corner, e.g. an online dot. */
  badge?: ReactNode;
  /** Buttons or chips on the right. */
  actions?: ReactNode;
  /** Somebody the player cannot pick right now. */
  dimmed?: boolean;
  /** A long list on a phone: a smaller face and less padding under 640 px. */
  compact?: boolean;
  className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>) {
  const banner = player?.activeBanner || null;
  return (
    <div
      {...rest}
      className={cn(
        'relative flex items-center overflow-hidden rounded-2xl border-[3px] border-slate-950 shadow-[0_4px_0_rgba(0,0,0,0.5)]',
        compact ? 'gap-2 p-1.5 sm:gap-3 sm:p-2.5' : 'gap-3 p-2.5',
        banner ? getBannerStyles(banner) : 'bg-slate-800',
        dimmed && 'opacity-50',
        className,
      )}
    >
      {banner && (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-950/25 rtl:bg-gradient-to-l" />
      )}
      <div className="relative shrink-0">
        <PlayerAvatar player={{ ...player, uid }} size="md" avatarClassName={compact ? 'h-8 w-8 sm:h-10 sm:w-10' : undefined} />
        {badge}
      </div>
      <div className="relative min-w-0 flex-1 text-start">
        <p className={cn('truncate text-sm font-black text-white', player?.activeNameEffect && getNameEffectClasses(player.activeNameEffect))}>
          {player?.displayName}
        </p>
        {detail}
      </div>
      {actions && <div className="relative flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
  );
}

/** The chunky button of the new design, without its colour: add `bg-primary text-primary-foreground` or a solid hue with `text-slate-950`. Disabled it goes solid and quiet (GAME_BUTTON_DISABLED_CLASS in game-look-classes.ts); add `gnc-game-keep-colour` to a disabled button that shows a pick. */
export const GAME_BUTTON_CLASS = 'rounded-2xl border-[3px] border-slate-950 font-black shadow-[0_5px_0_rgba(0,0,0,0.55)] gnc-game-press transition-transform active:translate-y-0.5 disabled:opacity-100 [&:disabled:not(.gnc-game-keep-colour)]:bg-muted [&:disabled:not(.gnc-game-keep-colour)]:text-foreground/70 [&:disabled:not(.gnc-game-keep-colour)]:shadow-none [.light_&:disabled:not(.gnc-game-keep-colour)]:border-[hsl(var(--border))] disabled:hover:translate-y-0 disabled:active:translate-y-0';

/** The same button small, for a row or a toolbar. */
export const GAME_BUTTON_SM_CLASS = 'rounded-xl border-[3px] border-slate-950 font-black shadow-[0_3px_0_rgba(0,0,0,0.55)] gnc-game-press transition-transform active:translate-y-0.5 disabled:opacity-100 [&:disabled:not(.gnc-game-keep-colour)]:bg-muted [&:disabled:not(.gnc-game-keep-colour)]:text-foreground/70 [&:disabled:not(.gnc-game-keep-colour)]:shadow-none [.light_&:disabled:not(.gnc-game-keep-colour)]:border-[hsl(var(--border))] disabled:hover:translate-y-0 disabled:active:translate-y-0';

/** A small label on a card: "YOUR WORD", "FRIENDS". */
export const GAME_LABEL_CLASS = 'text-[11px] font-black uppercase tracking-wider text-violet-300';

/** A pill of plain information: "Turn 9", "2 of 4 votes in". */
export const GAME_INFO_PILL_CLASS = 'gnc-game-display rounded-full border-2 border-white/15 bg-white/10 px-3 py-0.5 text-sm text-white';

// Faint text inside a GameResultRow: `text-white opacity-70`, never
// `text-white/70` or `text-muted-foreground`. Over a banner (a dark surface in
// both themes) those two turned dark ink in light mode, because
// tailwind.config.ts lifts `.light .text-white/NN` to ink with !important.
export const GAME_FAINT_ON_ROW = 'text-white opacity-70';

// ---------------------------------------------------------------------------
// Arcade glow (design direction B, 2026-10-08): on the pages around the games
// (shop, leaderboard, profile, account menu) a surface that means something is
// lit in its colour, like an arcade cabinet: a wash from the colour into the
// dark card, a 3px edge in the colour and a soft glow around it. The glow is
// still: nothing here pulses or loops.
//
// What each colour means: amber = the paid offer and Elite, emerald = free and
// owned, violet = you and Lite, cyan = credits, rose = missions and alerts,
// amber / silver / orange = gold, silver and bronze for the top three. The
// shop's tabs keep the hue their icon has always had.
//
// Light mode: the edge and the wash are hue shades, which mirror on their own
// (an amber-300 edge reads as amber-800 on the periwinkle page); the glows are
// literal rgba and stay as they are.
//
// The wash is drawn with `[background-image:linear-gradient(...)]` and the
// usual from-/via-/to- stops, not `bg-gradient-to-*`: tailwind-merge 3 reads
// `bg-gradient-to-*` as a background colour, so cn() dropped the card's own
// `bg-slate-900` under it and the coloured corner went see-through.
// ---------------------------------------------------------------------------
export type GameGlowHue =
  | 'amber' | 'emerald' | 'violet' | 'cyan' | 'rose' | 'sky' | 'blue' | 'indigo'
  | 'orange' | 'purple' | 'yellow' | 'teal' | 'pink' | 'silver';

export const GAME_GLOW: Record<GameGlowHue, {
  /** The 3px edge in the colour. */
  edge: string;
  /** The wash from the colour into the dark card. */
  wash: string;
  /** The same wash into a tile that sits on a card (slate-800). */
  tile: string;
  /** Hard drop shadow plus a soft glow around the card. Only where no parent clips it. */
  glow: string;
  /** Hard drop shadow plus the glow inside the edge, for a parent that clips (the shop's right-hand column). */
  glowIn: string;
  /** A small thing (a chip, a tab, a button) with its own halo. */
  glowSm: string;
  /** The solid colour, with the dark text it needs in both themes. */
  solid: string;
  /** Text and icons in the colour. */
  text: string;
}> = {
  amber: {
    edge: 'border-amber-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-amber-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-amber-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(252,211,77,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(252,211,77,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(252,211,77,0.5)]',
    solid: 'bg-amber-300 text-slate-950',
    text: 'text-amber-300',
  },
  emerald: {
    edge: 'border-emerald-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-emerald-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-emerald-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(110,231,183,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(110,231,183,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(110,231,183,0.5)]',
    solid: 'bg-emerald-300 text-slate-950',
    text: 'text-emerald-300',
  },
  violet: {
    edge: 'border-violet-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-violet-500/35 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-violet-500/35 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(167,139,250,0.4)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(167,139,250,0.24)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(167,139,250,0.6)]',
    solid: 'bg-violet-300 text-slate-950',
    text: 'text-violet-300',
  },
  cyan: {
    edge: 'border-cyan-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-cyan-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-cyan-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(103,232,249,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(103,232,249,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(103,232,249,0.5)]',
    solid: 'bg-cyan-300 text-slate-950',
    text: 'text-cyan-300',
  },
  rose: {
    edge: 'border-rose-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-rose-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-rose-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(251,113,133,0.35)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(251,113,133,0.22)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(251,113,133,0.55)]',
    solid: 'bg-rose-300 text-slate-950',
    text: 'text-rose-300',
  },
  sky: {
    edge: 'border-sky-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-sky-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-sky-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(125,211,252,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(125,211,252,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(125,211,252,0.5)]',
    solid: 'bg-sky-300 text-slate-950',
    text: 'text-sky-300',
  },
  blue: {
    edge: 'border-blue-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-blue-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-blue-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(147,197,253,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(147,197,253,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(147,197,253,0.5)]',
    solid: 'bg-blue-300 text-slate-950',
    text: 'text-blue-300',
  },
  indigo: {
    edge: 'border-indigo-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-indigo-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-indigo-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(165,180,252,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(165,180,252,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(165,180,252,0.5)]',
    solid: 'bg-indigo-300 text-slate-950',
    text: 'text-indigo-300',
  },
  orange: {
    edge: 'border-orange-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-orange-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-orange-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(253,186,116,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(253,186,116,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(253,186,116,0.5)]',
    solid: 'bg-orange-300 text-slate-950',
    text: 'text-orange-300',
  },
  purple: {
    edge: 'border-purple-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-purple-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-purple-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(216,180,254,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(216,180,254,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(216,180,254,0.5)]',
    solid: 'bg-purple-300 text-slate-950',
    text: 'text-purple-300',
  },
  yellow: {
    edge: 'border-yellow-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-yellow-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-yellow-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(253,224,71,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(253,224,71,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(253,224,71,0.5)]',
    solid: 'bg-yellow-300 text-slate-950',
    text: 'text-yellow-300',
  },
  teal: {
    edge: 'border-teal-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-teal-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-teal-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(94,234,212,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(94,234,212,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(94,234,212,0.5)]',
    solid: 'bg-teal-300 text-slate-950',
    text: 'text-teal-300',
  },
  pink: {
    edge: 'border-pink-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-pink-500/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-pink-500/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(249,168,212,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(249,168,212,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(249,168,212,0.5)]',
    solid: 'bg-pink-300 text-slate-950',
    text: 'text-pink-300',
  },
  // Second place. Slate is a neutral, so in light mode its edge and wash turn
  // to ink like every other neutral; the glow stays silver.
  silver: {
    edge: 'border-slate-300',
    wash: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-slate-300/30 via-slate-900 to-slate-900',
    tile: '[background-image:linear-gradient(to_bottom_right,var(--tw-gradient-stops))] from-slate-300/30 via-slate-800 to-slate-800',
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_28px_rgba(203,213,225,0.3)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_32px_rgba(203,213,225,0.2)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_16px_rgba(203,213,225,0.5)]',
    solid: 'bg-slate-300 text-slate-950',
    text: 'text-slate-300',
  },
};

// The shop's much weaker glow (Yahav, 2026-10-08: "too glowy", then "just make
// the glow in the shop weaker", and he took "Much weaker" from the preview): the
// same halos, half the size and at most a quarter as bright. The leaderboard and
// the profile keep the full glow.
export const GAME_GLOW_SOFT: Record<GameGlowHue, Pick<(typeof GAME_GLOW)[GameGlowHue], 'glow' | 'glowIn' | 'glowSm'>> = {
  amber: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(252,211,77,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(252,211,77,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(252,211,77,0.12)]',
  },
  emerald: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(110,231,183,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(110,231,183,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(110,231,183,0.12)]',
  },
  violet: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(167,139,250,0.09)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(167,139,250,0.06)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(167,139,250,0.14)]',
  },
  cyan: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(103,232,249,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(103,232,249,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(103,232,249,0.12)]',
  },
  rose: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(251,113,133,0.08)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(251,113,133,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(251,113,133,0.13)]',
  },
  sky: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(125,211,252,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(125,211,252,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(125,211,252,0.12)]',
  },
  blue: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(147,197,253,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(147,197,253,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(147,197,253,0.12)]',
  },
  indigo: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(165,180,252,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(165,180,252,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(165,180,252,0.12)]',
  },
  orange: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(253,186,116,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(253,186,116,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(253,186,116,0.12)]',
  },
  purple: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(216,180,254,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(216,180,254,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(216,180,254,0.12)]',
  },
  yellow: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(253,224,71,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(253,224,71,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(253,224,71,0.12)]',
  },
  teal: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(94,234,212,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(94,234,212,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(94,234,212,0.12)]',
  },
  pink: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(249,168,212,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(249,168,212,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(249,168,212,0.12)]',
  },
  silver: {
    glow: 'shadow-[0_6px_0_rgba(0,0,0,0.55),0_0_14px_rgba(203,213,225,0.07)]',
    glowIn: 'shadow-[0_6px_0_rgba(0,0,0,0.55),inset_0_0_16px_rgba(203,213,225,0.05)]',
    glowSm: 'shadow-[0_3px_0_rgba(0,0,0,0.55),0_0_8px_rgba(203,213,225,0.12)]',
  },
};

/**
 * A card lit in its colour: the edge, the wash and the glow. Put it after
 * GAME_CARD_CLASS in cn() so the edge and the shadow replace the card's own.
 * `clipped` keeps the glow inside the edge, for a parent that cuts anything
 * drawn outside it (the shop's right-hand column is overflow-x-clip).
 * `soft` takes the much weaker glow from GAME_GLOW_SOFT (the shop).
 */
export function gameGlowCard(hue: GameGlowHue, where: 'open' | 'clipped' = 'open', strength: 'full' | 'soft' = 'full'): string {
  const g = GAME_GLOW[hue];
  const halo = strength === 'soft' ? GAME_GLOW_SOFT[hue] : g;
  return cn(g.edge, g.wash, where === 'open' ? halo.glow : halo.glowIn);
}
